import * as stylex from '@stylexjs/stylex';
import { useQuery } from '@tanstack/react-query';
import { CatchBoundary, createFileRoute, redirect } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { useDateFormatter, useNumberFormatter } from 'react-aria';
import { nullish as vNullish, object as vObject, string as vString } from 'valibot';

import { PendingState } from '@/components/pending-state.component';
import { ReportEntityTypeEnum } from '@/libs/openapi';
import {
  EmptyState,
  Heading,
  tryCatch,
  useMediaQuery,
  View,
} from '@/libs/serenity-ui';
import { breakpoints } from '@/libs/serenity-ui/constants.stylex';
import { $ } from '@/libs/serenity-ui/tokens.stylex';
import { reportQueries } from '@/resources/report/report.api';
import { DownloadReportIconButton } from '@/routes/_main/_list/metrics/-components/download-report-icon-button.component';
import { PayoutTable } from '@/routes/_main/_list/metrics/-components/payout-table.component';
import { PerformanceChart } from '@/routes/_main/_list/metrics/-components/performance-chart.component';
import { PerformanceTable } from '@/routes/_main/_list/metrics/-components/performance-table.component';
import { ReportSelector } from '@/routes/_main/_list/metrics/-components/report-selector.component';
import { ReportTypeSelector } from '@/routes/_main/_list/metrics/-components/report-type-selector.component';
import { ToggleExpandableRowsButton } from '@/routes/_main/_list/metrics/-components/toggle-expandable-rows-button.component';
import { Resource } from '@/types/resource.type';

const PAYOUT_REPORT_TYPE = 'ad_set_payout_publisher';

export const Route = createFileRoute('/_main/_list/metrics/')({
  validateSearch: vObject({
    reportType: vNullish(vString()),
    reportIndex: vNullish(vString()),
  }),
  loaderDeps: ({ search }) => search,
  beforeLoad: ({ context }) => {
    if (context.user !== undefined && !context.can.reports.view()) {
      throw redirect({ to: '/' });
    }

    return {
      resource: Resource.report as const,
      queryOptions: {
        types: reportQueries.types,
        list: (type: string) => reportQueries.list({ type }),
      },
    };
  },
  loader: async ({ deps, context }) => {
    if (!context.user) {
      return;
    }

    const { data: types } = await tryCatch(context.queryClient.ensureQueryData(context.queryOptions.types));
    if (!types?.length) {
      return;
    }

    const activeType = deps.reportType ?? types[0]?.original.type ?? '';

    const { data: reportsList } = await tryCatch(context.queryClient.ensureQueryData(context.queryOptions.list(activeType)));

    const selectedReport = reportsList?.find((r) => r.csvFileName === deps.reportIndex) ?? reportsList?.at(-1);

    if (selectedReport) {
      if (activeType === PAYOUT_REPORT_TYPE) {
        context.queryClient.prefetchQuery(reportQueries.payoutContent({
          type: activeType,
          entityType: selectedReport.entityType,
          fileName: selectedReport.csvFileName,
        }));
      } else {
        context.queryClient.prefetchQuery(reportQueries.content({
          type: activeType,
          entityType: selectedReport.entityType,
          fileName: selectedReport.csvFileName,
        }));
      }
    }
  },
  component: RouteComponent,
  pendingComponent: PendingState,
  staticData: {
    list: {
      heading: 'Analytics',
      slots: {
        toolbarLeftComponent: ToolbarLeftComponent,
        toolbarActionsComponent: ToolbarActionsComponent,
      },
    },
  },
  head: () => ({
    meta: [
      {
        title: `TrendMD | Analytics`,
      },
    ],
  }),
});

function RouteComponent (): ReactElement {
  const routeContext = Route.useRouteContext();
  const search = Route.useSearch();

  const typesQuery = useQuery(routeContext.queryOptions.types);
  const activeType = search.reportType ?? typesQuery.data?.[0]?.original.type ?? '';
  const isPayoutView = activeType === PAYOUT_REPORT_TYPE;

  const reportsQuery = useQuery({
    ...routeContext.queryOptions.list(activeType),
    enabled: !!activeType,
  });

  const selectedReport = reportsQuery.data?.find((r) => r.csvFileName === search.reportIndex) ?? reportsQuery.data?.at(-1);

  const fileContentQuery = useQuery({
    ...reportQueries.content({
      type: activeType,
      entityType: selectedReport?.entityType ?? ReportEntityTypeEnum.ORGANIZATION,
      fileName: selectedReport?.csvFileName ?? '',
    }),
    enabled: !isPayoutView && !!selectedReport,
  });

  const payoutContentQuery = useQuery({
    ...reportQueries.payoutContent({
      type: activeType,
      entityType: selectedReport?.entityType ?? ReportEntityTypeEnum.AD_SET,
      fileName: selectedReport?.csvFileName ?? '',
    }),
    enabled: isPayoutView && !!selectedReport,
  });

  const shortDateFormatter = useDateFormatter({ month: 'short' });
  const dateFormatter = useDateFormatter({ month: 'short', day: '2-digit', year: 'numeric' });

  const compactNumberFormatter = useNumberFormatter({ notation: 'compact' });
  const numberFormatter = useNumberFormatter({});

  const isLoading =
    typesQuery.isLoading ||
    reportsQuery.isLoading ||
    (isPayoutView ? payoutContentQuery.isLoading : fileContentQuery.isLoading);

  const hasReports = !isLoading && !!reportsQuery.data?.length;

  if (isLoading) {
    return <PendingState />;
  }

  if (isPayoutView) {
    return (
      <PayoutTable
        data={hasReports ? payoutContentQuery.data : undefined}
        organizationName={routeContext.organization.original.name}
      />
    );
  }

  return hasReports ? (
    <View style={pageStyles.scrollArea}>
      {hasReports &&
      <CatchBoundary getResetKey={() => 'reset'} onCatch={(error, errorInfo) => console.error(error, errorInfo)}>
        <View style={chartsViewStyles.base}>
          <PerformanceChart
            color={$.accentSky60}
            data={fileContentQuery.data?.series?.totalTrafficLift ?? []}
            dateFormatter={dateFormatter}
            label="Total traffic lift"
            shortDateFormatter={shortDateFormatter}
            yTickFormatter={(value) => `${value}%`}
            crosshairItems={[
              {
                color: $.accentCucumber80,
                formatValue: (value) => numberFormatter.format(value),
                getValue: (x) => fileContentQuery.data?.series?.widgetUsers.find((point) => point.x === x)?.y ?? 0,
                label: 'Widget users',
              },
              {
                color: $.accentChilli60,
                formatValue: (value) => numberFormatter.format(value),
                getValue: (x) => fileContentQuery.data?.series?.additionalPageViews.find((point) => point.x === x)?.y ?? 0,
                label: 'Additional page views',
              },
            ]}
          />
          <PerformanceChart
            color={$.accentCucumber80}
            data={fileContentQuery.data?.series?.widgetUsers ?? []}
            dateFormatter={dateFormatter}
            label="Widget users"
            shortDateFormatter={shortDateFormatter}
            yTickFormatter={(value) => compactNumberFormatter.format(value)}
            crosshairItems={[
              {
                color: $.accentSky60,
                formatValue: (value) => `${value}%`,
                getValue: (x) => fileContentQuery.data?.series?.totalTrafficLift.find((point) => point.x === x)?.y ?? 0,
                label: 'Total traffic lift',
              },
              {
                color: $.accentChilli60,
                formatValue: (value) => numberFormatter.format(value),
                getValue: (x) => fileContentQuery.data?.series?.additionalPageViews.find((point) => point.x === x)?.y ?? 0,
                label: 'Additional page views',
              },
            ]}
          />
          <PerformanceChart
            color={$.accentChilli60}
            data={fileContentQuery.data?.series?.additionalPageViews ?? []}
            dateFormatter={dateFormatter}
            label="Additional page views"
            shortDateFormatter={shortDateFormatter}
            yTickFormatter={(value) => compactNumberFormatter.format(value)}
            crosshairItems={[
              {
                color: $.accentSky60,
                formatValue: (value) => `${value}`,
                getValue: (x) => fileContentQuery.data?.series?.totalTrafficLift.find((point) => point.x === x)?.y ?? 0,
                label: 'Total traffic lift',
              },
              {
                color: $.accentCucumber80,
                formatValue: (value) => numberFormatter.format(value),
                getValue: (x) => fileContentQuery.data?.series?.widgetUsers.find((point) => point.x === x)?.y ?? 0,
                label: 'Widget users',
              },
            ]}
          />
        </View>
      </CatchBoundary>}
      <CatchBoundary getResetKey={() => 'reset'} onCatch={(error, errorInfo) => console.error(error, errorInfo)}>
        <PerformanceTable columns={fileContentQuery.data?.columns} rows={fileContentQuery.data?.rows} />
      </CatchBoundary>
    </View>
  ) : (
    <EmptyState style={emptyStateStyles.base}>
      <Heading>No analytics for {routeContext.organization.original.name}</Heading>
    </EmptyState>
  );
}

function ToolbarLeftComponent (): ReactElement | null {
  const isSmallScreen = useMediaQuery(breakpoints.small);

  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const routeContext = Route.useRouteContext();

  const typesQuery = useQuery(routeContext.queryOptions.types);
  const activeType = search.reportType ?? typesQuery.data?.[0]?.original.type ?? '';
  const isPayoutView = activeType === PAYOUT_REPORT_TYPE;

  const reportsQuery = useQuery({
    ...routeContext.queryOptions.list(activeType),
    enabled: !!activeType,
  });

  const selectedReport =
    reportsQuery.data?.find((r) => r.csvFileName === search.reportIndex) ??
    reportsQuery.data?.at(-1);

  if (isSmallScreen) {
    return (
      <>
        {isPayoutView && <ToggleExpandableRowsButton />}
        <DownloadReportIconButton type={activeType} />
      </>
    );
  }

  return (
    <View slot={null!} style={toolbarSelectorsStyles.base}>
      <ReportTypeSelector
        selectedType={activeType}
        types={typesQuery.data ?? []}
        onSelectionChange={(type) => navigate({ to: '/metrics', search: { reportType: type, reportIndex: undefined }})}
      />
      <ReportSelector
        reports={reportsQuery.data ?? []}
        selectedReport={selectedReport}
        onSelectionChange={(csvFileName) => navigate({ to: '/metrics', search: (prev) => ({ ...prev, reportIndex: csvFileName }) })}
      />
    </View>
  );
}

function ToolbarActionsComponent (): ReactElement | null {
  const isSmallScreen = useMediaQuery(breakpoints.small);

  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const routeContext = Route.useRouteContext();

  const typesQuery = useQuery(routeContext.queryOptions.types);
  const activeType = search.reportType ?? typesQuery.data?.[0]?.original.type ?? '';
  const isPayoutView = activeType === PAYOUT_REPORT_TYPE;

  const reportsQuery = useQuery({
    ...routeContext.queryOptions.list(activeType),
    enabled: !!activeType,
  });

  const selectedReport =
    reportsQuery.data?.find((r) => r.csvFileName === search.reportIndex) ??
    reportsQuery.data?.at(-1);

  if (isSmallScreen) {
    return (
      <View slot={null!} style={toolbarSelectorsStyles.base}>
        <ReportTypeSelector
          selectedType={activeType}
          types={typesQuery.data ?? []}
          onSelectionChange={(type) => navigate({ to: '/metrics', search: { reportType: type, reportIndex: undefined }})}
        />
        <ReportSelector
          reports={reportsQuery.data ?? []}
          selectedReport={selectedReport}
          onSelectionChange={(csvFileName) => navigate({ to: '/metrics', search: (prev) => ({ ...prev, reportIndex: csvFileName }) })}
        />
      </View>
    );
  }

  return (
    <View slot={null!} style={toolbarSelectorsStyles.base}>
      {isPayoutView && <ToggleExpandableRowsButton />}
      <DownloadReportIconButton type={activeType} />
    </View>
  );
}

const toolbarSelectorsStyles = stylex.create({
  base: {
    alignItems: 'center',
    display: 'flex',
    gap: {
      default: 16,
      [breakpoints.small]: 8,
    },
    width: '100%',
  },
});

const pageStyles = stylex.create({
  scrollArea: {
    height: '100%',
    overflowX: 'hidden',
    overflowY: 'auto',
    width: '100%',
  },
});

const chartsViewStyles = stylex.create({
  base: {
    display: 'grid',
    gap: 17,
    gridTemplateColumns: {
      default: '1fr 1fr 1fr',
      [breakpoints.medium]: '1fr 1fr',
      [breakpoints.small]: '1fr',
    },
    paddingBottom: 24,
    paddingInline: 16,
    paddingTop: 16,
    width: '100%',
  },
});

const emptyStateStyles = stylex.create({
  base: {
    paddingTop: 90,
  },
});
